// Components - Static imports for critical routes
import App from '@src/components/app/App';
import NotFound from '@src/components/screen/404';
import Index from '@src/components/screen/index';
import { FeatureFlag, featureFlagManager } from '@src/config/featureFlags';
import { RouterPath } from '@src/constants/router_path';
import React from 'react';
import { createBrowserRouter, type RouteObject } from 'react-router-dom';

// Lazy loaded components for code splitting
const Contact = React.lazy(() => import('@src/components/screen/resume/contact/index'));
const EducationalBackground1 = React.lazy(
  () => import('@src/components/screen/resume/educational_background/1')
);
const EducationalBackground2 = React.lazy(
  () => import('@src/components/screen/resume/educational_background/2')
);
const History = React.lazy(() => import('@src/components/screen/resume/history/index'));
const HistoryComment = React.lazy(
  () => import('@src/components/screen/resume/history_comment/index')
);
const CheckIputStatus = React.lazy(
  () => import('@src/components/screen/resume/input_status/index')
);
const NextCareer = React.lazy(() => import('@src/components/screen/resume/next_career/index'));
const OtherActivity = React.lazy(
  () => import('@src/components/screen/resume/private_activity/index')
);
const SelectMode = React.lazy(() => import('@src/components/screen/resume/private_mode/index'));
const Project = React.lazy(() => import('@src/components/screen/resume/project/index'));
const Skill1 = React.lazy(() => import('@src/components/screen/resume/skill/1'));
const Skill2 = React.lazy(() => import('@src/components/screen/resume/skill/2'));
const Summary = React.lazy(() => import('@src/components/screen/resume/summary/index'));
const FAQ = React.lazy(() => import('@src/components/screen/FAQ'));
const DataExportImport = React.lazy(() => import('@src/components/screen/data'));

// 履歴書コンポーネント - Lazy loaded
const RirekishoBasicInfo = React.lazy(
  () => import('@src/components/screen/rirekisho/basic-info/index')
);
const RirekishoPhoto = React.lazy(() => import('@src/components/screen/rirekisho/photo/index'));
const RirekishoAddress = React.lazy(() => import('@src/components/screen/rirekisho/address/index'));
const RirekishoContact = React.lazy(() => import('@src/components/screen/rirekisho/contact/index'));
const RirekishoHistory = React.lazy(() => import('@src/components/screen/rirekisho/history/index'));
const RirekishoLicense = React.lazy(() => import('@src/components/screen/rirekisho/license/index'));
const RirekishoMotivation = React.lazy(
  () => import('@src/components/screen/rirekisho/motivation/index')
);
const RirekishoPersonalRequests = React.lazy(
  () => import('@src/components/screen/rirekisho/personal-requests/index')
);
const RirekishoInputStatus = React.lazy(
  () => import('@src/components/screen/rirekisho/input-status/index')
);

// 認証コンポーネント - Lazy loaded (only if auth is enabled)
const Login = React.lazy(() => import('@src/components/screen/auth/Login'));
const Register = React.lazy(() => import('@src/components/screen/auth/Register'));
const MfaVerify = React.lazy(() => import('@src/components/screen/auth/MfaVerify'));
const PasswordResetRequest = React.lazy(
  () => import('@src/components/screen/auth/PasswordResetRequest')
);
const PasswordResetConfirm = React.lazy(
  () => import('@src/components/screen/auth/PasswordResetConfirm')
);
const AccountWithdrawal = React.lazy(() => import('@src/components/screen/auth/AccountWithdrawal'));
const ProfilePage = React.lazy(() =>
  import('@src/components/screen/user').then((module) => ({ default: module.ProfilePage }))
);

// Loading fallback component
const LoadingFallback = () => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '200px',
      padding: '20px',
    }}
  >
    読み込み中...
  </div>
);

type LazyRouteConfig = {
  path: RouterPath;
  component: React.LazyExoticComponent<React.ComponentType<any>>;
};

const withSuspense = (
  Component: React.LazyExoticComponent<React.ComponentType<any>>
): React.ReactElement => (
  <React.Suspense fallback={<LoadingFallback />}>
    <Component />
  </React.Suspense>
);

const createLazyRoutes = (configs: LazyRouteConfig[]): RouteObject[] =>
  configs.map(({ path, component }) => ({
    path,
    element: withSuspense(component),
  }));

const resumeRoutes: LazyRouteConfig[] = [
  { path: RouterPath.inputStatus, component: CheckIputStatus },
  { path: RouterPath.contact, component: Contact },
  { path: RouterPath.nextCareer, component: NextCareer },
  { path: RouterPath.otherActivity, component: OtherActivity },
  { path: RouterPath.project, component: Project },
  { path: RouterPath.skill1, component: Skill1 },
  { path: RouterPath.skill2, component: Skill2 },
  { path: RouterPath.careerSummary, component: Summary },
  { path: RouterPath.historyComment, component: HistoryComment },
  { path: RouterPath.history, component: History },
  { path: RouterPath.educationalBackground1, component: EducationalBackground1 },
  { path: RouterPath.educationalBackground2, component: EducationalBackground2 },
  { path: RouterPath.selectMode, component: SelectMode },
  { path: RouterPath.faq, component: FAQ },
  { path: RouterPath.exportImport, component: DataExportImport },
];

const rirekishoRoutes: LazyRouteConfig[] = [
  { path: RouterPath.rirekishoBasicInfo, component: RirekishoBasicInfo },
  { path: RouterPath.rirekishoPhoto, component: RirekishoPhoto },
  { path: RouterPath.rirekishoAddress, component: RirekishoAddress },
  { path: RouterPath.rirekishoContact, component: RirekishoContact },
  { path: RouterPath.rirekishoHistory, component: RirekishoHistory },
  { path: RouterPath.rirekishoLicense, component: RirekishoLicense },
  { path: RouterPath.rirekishoMotivation, component: RirekishoMotivation },
  { path: RouterPath.rirekishoPersonalRequests, component: RirekishoPersonalRequests },
  { path: RouterPath.rirekishoInputStatus, component: RirekishoInputStatus },
];

const authRoutes: LazyRouteConfig[] = [
  { path: RouterPath.login, component: Login },
  { path: RouterPath.register, component: Register },
  { path: RouterPath.mfaVerify, component: MfaVerify },
  { path: RouterPath.passwordResetRequest, component: PasswordResetRequest },
  { path: RouterPath.passwordResetConfirm, component: PasswordResetConfirm },
  { path: RouterPath.accountWithdrawal, component: AccountWithdrawal },
  { path: RouterPath.profile, component: ProfilePage },
];

// 認証機能が有効な場合のみ認証ルートを含める
const enabledAuthRoutes = featureFlagManager.isEnabled(FeatureFlag.AUTH_ENABLED)
  ? createLazyRoutes(authRoutes)
  : [];

// 履歴書機能が有効な場合のみ履歴書ルートを含める
const enabledRirekishoRoutes = featureFlagManager.isEnabled(FeatureFlag.USE_RIREKISHO)
  ? createLazyRoutes(rirekishoRoutes)
  : [];

// 経歴書機能が有効な場合のみ経歴書ルートを含める
const enabledResumeRoutes = featureFlagManager.isEnabled(FeatureFlag.USE_RESUME)
  ? createLazyRoutes(resumeRoutes)
  : [];

const router = createBrowserRouter([
  {
    element: <App />,
    children: [
      ...enabledResumeRoutes,
      ...enabledRirekishoRoutes,
      ...enabledAuthRoutes,
      {
        path: RouterPath.index,
        element: <Index />,
      },
      {
        path: '*',
        element: <NotFound />,
      },
    ],
  },
]);

export default router;
